import type { LoadRequest, PlayerInterface } from "@foundation-player/api";
import { Media } from "./media_player.types";
import { Plugin } from "./media_plugin";
import { storageAvailable } from "./utils";

export type MediaPositions = {
  [key: string]: number;
};

export const MEDIA_POSITION_STORAGE_KEY = "ws-media.position";

/**
 * Plugin to save the current position.
 *
 * More about event handling and state management:
 * @see https://docs.player.foundation/v2/api/type/player-interface/#state
 * @see https://docs.player.foundation/v2/api/type/observable/
 * @see https://docs.player.foundation/v2/api/type/player-state/
 *
 * Examples can be found in section "4 Update event handling and state management"
 * @see https://docs.player.foundation/v2/integration/migration/#migration-steps
 */
export class PluginPosition extends Plugin {
  readonly #isEnabled: boolean;
  readonly #contentId: string | undefined;
  readonly #interval: number = 5;
  #lastSavedTime: number | undefined;
  #positions: MediaPositions = {};

  public constructor(media: Media) {
    super(media);
    this.#isEnabled = storageAvailable("localStorage") && !!media.id;
    this.#contentId = media.id;
  }

  public isEnabled(): boolean {
    return this.#isEnabled;
  }

  // https://docs.player.foundation/v2/api/type/load-request/#starttimeinseconds
  public addFoundationLoadRequestProperties(): Partial<LoadRequest> {
    this.#positions = this.#getPositions();
    this.#lastSavedTime = this.#positions[this.#contentId!] || 0;
    return {
      startTimeInSeconds: this.#lastSavedTime,
    };
  }

  public addFoundationEventListener(
    foundationPlayer: PlayerInterface,
    _element: HTMLElement,
  ): void {
    foundationPlayer.state.onValueChange(
      "currentTimeInSeconds",
      ({ currentTimeInSeconds, durationInSeconds, adActive }) => {
        if (adActive) return;

        const newTime = Math.floor(currentTimeInSeconds);
        const duration = Math.floor(durationInSeconds || 0);

        if (newTime && duration && newTime >= duration) {
          this.#removePosition();
        } else if (
          this.#lastSavedTime !== newTime &&
          newTime % this.#interval === 0
        ) {
          this.#savePosition(newTime);
        }
      },
    );
  }

  #getPositions = (): MediaPositions => {
    const data = localStorage.getItem(MEDIA_POSITION_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    } else {
      return {};
    }
  };

  #savePosition = (newTime: number) => {
    this.#positions = this.#getPositions();
    this.#positions[this.#contentId!] = newTime;
    localStorage.setItem(
      MEDIA_POSITION_STORAGE_KEY,
      JSON.stringify(this.#positions),
    );
    this.#lastSavedTime = newTime;
  };

  #removePosition = () => {
    delete this.#positions[this.#contentId!];
    localStorage.setItem(
      MEDIA_POSITION_STORAGE_KEY,
      JSON.stringify(this.#positions),
    );
    this.#lastSavedTime = 0;
  };
}
